import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { downloadPdfs } from './downloadPdfs'
import { getPdfLabelByType, getPdfLabelsByTypes } from './getPdfLabelsByTypes'
import { Icon, IconSvg, Spinner } from '../../atoms'
import {
  useAdminCanUpdate,
  useLogsContext,
  useUserTypeContext,
} from '../../contexts'
import { usePdfContext } from '../../contexts/pdf-context/usePdfContext'
import { LogTypeEnum, PdfOutputDto, PdfTypeEnum } from '../../../lib/graphql'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../shadcn/components/ui/dropdown-menu'
import { cn } from '../../../shadcn/utils'

export interface PdfsRef extends HTMLDivElement {
  downloadPdfs: () => void
}

export const DownloadPdfDropdown = ({
  clientRequestId,
  clientProposalId,
  houseId,
  triggerClassName,
  dropdownMenuSide = 'top',
}: {
  clientRequestId: string
  clientProposalId: string
  triggerClassName?: string
  houseId?: string
  dropdownMenuSide?: 'bottom' | 'top'
}) => {
  const intl = useIntl()
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
  const [pdfTypesLoadingForRegeneration, setPdfTypesLoadingForRegeneration] =
    useState<Set<PdfTypeEnum>>(new Set())

  const { adminCanUpdate } = useAdminCanUpdate()

  const downloadPdfLocked = adminCanUpdate === false

  const { createUserLog } = useLogsContext()

  const {
    pdfTypes,
    getPdfs,
    isGeneratingDocuments,
    useGetPdfsByClientProposalIdQuery,
  } = usePdfContext()
  const { isAdmin } = useUserTypeContext()

  const {
    data: pdfs,
    isLoading: isPdfsLoading,
    isError: isPdfError,
  } = useGetPdfsByClientProposalIdQuery({
    pdfTypes,
    clientProposalId,
    clientRequestId,
  })

  const regenerateDocument = async (
    pdfType: PdfTypeEnum,
  ): Promise<Pick<PdfOutputDto, 'filename' | 'url'> | null> => {
    const isPdfLoading = pdfTypesLoadingForRegeneration.has(pdfType)
    if (isPdfLoading) {
      return null
    }

    setPdfTypesLoadingForRegeneration(
      pdfTypesLoadingForRegeneration.add(pdfType),
    )

    try {
      const pdf = await getPdfs([pdfType], true)
      await downloadPdfs(pdf)

      pdfTypesLoadingForRegeneration.delete(pdfType)
      setPdfTypesLoadingForRegeneration(pdfTypesLoadingForRegeneration)

      return pdf[0]
    } catch (error) {
      console.error('Failed to regenerate PDF:', error)
      return null
    }
  }

  const handleDownload = async ({
    pdfType,
    shouldCreateLog = true,
  }: {
    pdfType: PdfTypeEnum
    shouldCreateLog?: boolean
  }) => {
    if (shouldCreateLog) {
      const pdfDownloaded = getPdfLabelByType({ pdfType, intl })

      createUserLog({
        type: LogTypeEnum.CLICK_ON_DOWNLOAD_DOCUMENT,
        messages: [pdfDownloaded],
        clientRequestId,
        houseId,
      })
    }

    if (isPdfsLoading || isPdfError) {
      return
    }
    const pdf = pdfs.find((pdf) => pdf.pdfType === pdfType)

    if (pdf) {
      await downloadPdfs([pdf])
    } else {
      await regenerateDocument(pdfType)
    }
  }

  const handleDownloadAll = async () => {
    const pdfDownloaded = getPdfLabelsByTypes({ pdfTypes, intl })
    createUserLog({
      type: LogTypeEnum.CLICK_ON_DOWNLOAD_ALL_DOCUMENTS,
      messages: pdfDownloaded,
      clientRequestId,
      houseId,
    })

    if (isPdfsLoading || isPdfError) {
      return
    }

    // Collect all valid PDFs
    const allPdfsToDownload = await Promise.all(
      pdfTypes.map(async (pdfType) => {
        const pdf = pdfs.find((pdf) => pdf.pdfType === pdfType)

        if (pdf) {
          return pdf
        } else {
          const regeneratedPdf = await regenerateDocument(pdfType)
          return regeneratedPdf
        }
      }),
    )

    // Filter out any null/undefined values and handle missing urls safely
    const validPdfsToDownload = allPdfsToDownload.filter(
      (pdf): pdf is Pick<PdfOutputDto, 'filename' | 'url'> => {
        if (!pdf?.url || !pdf?.filename) {
          console.error(`PDF is missing 'url' or 'filename':`, pdf)
          return false
        }
        return true
      },
    )

    if (validPdfsToDownload.length > 0) {
      await downloadPdfs(validPdfsToDownload)
    }
  }

  if (!pdfTypes.length) {
    return null
  }

  if (!houseId) {
    return null
  }

  return (
    <DropdownMenu onOpenChange={(open) => setDropdownIsOpen(open)}>
      <DropdownMenuTrigger
        className={cn(
          'border-secondary-foreground h-full w-[265px] rounded-md border border-solid bg-white px-4 py-2 text-sm',
          { ['cursor-pointer']: !isGeneratingDocuments && !downloadPdfLocked },
          'disabled:bg-muted',
          triggerClassName,
        )}
        disabled={isGeneratingDocuments || downloadPdfLocked}
      >
        <>
          {isGeneratingDocuments ? (
            <div className='flex w-full items-center justify-between'>
              <p className='text-sm'>
                <FormattedMessage
                  id='ui.DownloadPdfDropdown.generateDocs'
                  defaultMessage='Génération des documents'
                />
              </p>
              <Spinner color='black' width={14} height={14} />
            </div>
          ) : (
            <div className='flex items-center gap-3'>
              <p className='text-sm'>
                <FormattedMessage
                  id='ui.DownloadPdfDropdown.downloadDocs'
                  defaultMessage='Télécharger les documents'
                />
              </p>
              {downloadPdfLocked ? (
                <IconSvg.Lock className='ml-auto' />
              ) : (
                <div className='ml-auto flex items-center'>
                  <Icon name='paperclip' className='h-[14px] w-[14px]' />
                  <p>{pdfTypes.length.toString()}</p>
                  <Icon
                    name='arrow-down'
                    size={12}
                    className={cn('ml-3 transition ease-in-out', {
                      ['rotate-180']: dropdownIsOpen,
                    })}
                  />
                </div>
              )}
            </div>
          )}
        </>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className='z-[100001] max-h-80 w-[--radix-popper-anchor-width] overflow-y-auto'
        side={dropdownMenuSide}
      >
        <div className='shadow-black/20 shadow-md'>
          <ul>
            {pdfTypes.map((pdfType, index) => {
              return (
                <React.Fragment key={index}>
                  <li className='flex justify-start px-6 py-4'>
                    <div className='flex cursor-pointer items-center gap-2 hover:underline'>
                      <div>
                        {pdfTypesLoadingForRegeneration.has(pdfType) ? (
                          <Spinner color='gradient' width={14} height={14} />
                        ) : (
                          <Icon name='paperclip' className='h-4 w-4' />
                        )}
                      </div>
                      <p
                        onClick={() => handleDownload({ pdfType })}
                        className={cn('text-sm', { ['mr-2']: isAdmin })}
                      >
                        {getPdfLabelsByTypes({ pdfTypes: [pdfType], intl })}
                      </p>
                    </div>
                    {isAdmin && (
                      <div
                        className='ml-auto flex items-center'
                        onClick={async () => await regenerateDocument(pdfType)}
                      >
                        <IconSvg.Reset
                          color='#0ebc8b'
                          className={cn(
                            'h-5 w-5 cursor-pointer transition ease-in-out',
                            'hover:scale-125',
                          )}
                        />
                      </div>
                    )}
                  </li>
                  {index < pdfTypes.length - 1 && (
                    <DropdownMenuSeparator className='my-0 bg-grey-400' />
                  )}
                </React.Fragment>
              )
            })}
            <DropdownMenuSeparator className='my-0 bg-grey-400' />
            <li
              onClick={async () => await handleDownloadAll()}
              className='flex px-6 py-4'
            >
              <p className={cn('cursor-pointer text-sm', 'hover:underline')}>
                <FormattedMessage
                  id='ui.DownloadPdfDropdown.downloadAllDocs'
                  defaultMessage='Tout enregistrer'
                />
              </p>
            </li>
          </ul>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
